import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { ArrowRight, BarChart3, Settings, Sparkles, CheckCircle } from "lucide-react";
import AccountFloatingButton from "@/components/AccountFloatingButton";
import { useClientAuth } from "@/hooks/useClientAuth";
import { useHomeClientKpis } from "@/hooks/useHomeClientKpis";

const Home = () => {
  const { clientId } = useParams();
  const { isAuthenticated, loading: authLoading } = useClientAuth();
  const base = clientId ? `/${clientId}` : "";

  // KPI calculés via le même pipeline que la Synthèse
  const { kpis, loading: kpisLoading, roiEnabled } = useHomeClientKpis(
    clientId && isAuthenticated ? clientId : undefined
  );

  // 3 états : pas de client, loading, authentifié
  const isClientRoute = !!clientId;
  const isResolving = isClientRoute && (authLoading || (isAuthenticated && kpisLoading));
  const showClientContent = isClientRoute && isAuthenticated && !authLoading;
  
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-10"></div>
        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-primary bg-clip-text text-transparent leading-tight">
            Automatisez le présent. Accélérez le futur.
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-12 leading-relaxed">
            Identifiez votre potentiel d'automatisation et d'amélioration, décuplez l'efficacité de vos équipes et concentrez-vous sur ce qui compte vraiment : votre croissance
            </p>
            
            {/* Statistiques impactantes */}
            {isResolving ? (
              // Placeholders neutres pendant la résolution auth / chargement KPI
              <div className="grid md:grid-cols-4 gap-6 mb-12">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="text-center animate-pulse">
                    <div className="h-10 w-24 mx-auto mb-2 bg-muted rounded" />
                    <div className="h-4 w-32 mx-auto bg-muted rounded" />
                  </div>
                ))}
              </div>
            ) : showClientContent && roiEnabled && kpis ? (
              // KPI dynamiques du client (même pipeline que la Synthèse)
              <div className="grid md:grid-cols-4 gap-6 mb-12">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {kpis.netBenefit}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    de bénéfice net sur {kpis.projectDurationYears} an{kpis.projectDurationYears > 1 ? 's' : ''}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-2">
                    {kpis.equivalentFTE}
                  </div>
                  <div className="text-sm text-muted-foreground">ETP gagnables</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {kpis.globalROI}
                  </div>
                  <div className="text-sm text-muted-foreground">de ROI moyen</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-accent mb-2">
                    {kpis.amortization}
                  </div>
                  <div className="text-sm text-muted-foreground">d'amortissement</div>
                </div>
              </div>
            ) : (
              // Statistiques marketing Ailix (pas de client ou roi_enabled=false)
              <>
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary mb-2">60+</div>
                    <div className="text-sm text-muted-foreground">Projets réalisés</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-accent mb-2">98%</div>
                    <div className="text-sm text-muted-foreground">Réussite sur nos projets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary mb-2">3 ans</div>
                    <div className="text-sm text-muted-foreground">Expérience en IA générative</div>
                  </div>
                </div>
                <div className="grid md:grid-cols-1 gap-6 mb-12 max-w-sm mx-auto">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-accent mb-2">100%</div>
                    <div className="text-sm text-muted-foreground">Hébergement FR</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Section équipe technique externalisée */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 rounded-full mb-8">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-primary font-medium">Notre offre unique</span>
            </div>
            
            <h2 className="text-4xl font-bold mb-6 text-primary">
              Votre équipe technique IA externalisée
            </h2>
            <p className="text-xl text-muted-foreground mb-4 leading-relaxed max-w-4xl mx-auto">
              Une équipe complète de développeurs, chef de projet technique et expert IA à votre disposition.
            </p>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed max-w-4xl mx-auto">
              <strong>Tous les avantages d'une équipe interne, sans les contraintes</strong>.
            </p>
          </div>

          {/* Tableau comparatif équipe */}
          <div className="max-w-6xl mx-auto mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-primary/10 overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-3">
                {/* Colonne Équipe Interne */}
                <div className="p-8 border-r border-gray-200">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-600 mb-2">Équipe Interne</h3>
                    <p className="text-sm text-muted-foreground">Recrutement & management</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Recrutement</span>
                      <span className="text-red-600 font-semibold">Complexe</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Management quotidien</span>
                      <span className="text-red-600 font-semibold">À votre charge</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts cachés</span>
                      <span className="text-red-600 font-semibold">Élevés</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Flexibilité missions</span>
                      <span className="text-yellow-600 font-semibold">Limitée</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Expertise IA</span>
                      <span className="text-red-600 font-semibold">Variable</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Risques sociaux</span>
                      <span className="text-red-600 font-semibold">Élevés</span>
                    </div>
                  </div>
                </div>

                {/* Colonne Expert IA */}
                <div className="p-8 border-r border-gray-200">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-yellow-600 mb-2">Experts IA</h3>
                    <p className="text-sm text-muted-foreground">Freelance ou cabinet spécialisé</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Recrutement</span>
                      <span className="text-yellow-600 font-semibold">Difficile</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Management quotidien</span>
                      <span className="text-yellow-600 font-semibold">Nécessaire</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts cachés</span>
                      <span className="text-yellow-600 font-semibold">Moyens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Flexibilité missions</span>
                      <span className="text-yellow-600 font-semibold">Bonne</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Expertise IA</span>
                      <span className="text-green-600 font-semibold">Excellente</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Risques sociaux</span>
                      <span className="text-yellow-600 font-semibold">Moyens</span>
                    </div>
                  </div>
                </div>

                {/* Colonne Ailix */}
                <div className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-primary mb-2">Ailix</h3>
                    <p className="text-sm text-primary/80">Équipe complète & spécialisée clé en main</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Recrutement</span>
                      <span className="text-primary font-bold">Nos équipes</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Management quotidien</span>
                      <span className="text-primary font-bold">Inclus</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts cachés</span>
                      <span className="text-primary font-bold">Aucun</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Flexibilité missions</span>
                      <span className="text-primary font-bold">Maximale</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Expertise IA</span>
                      <span className="text-primary font-bold">Expert dédié</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Risques sociaux</span>
                      <span className="text-primary font-bold">Zéro</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section avantages financiers */}
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-primary/10">
                <h3 className="text-xl font-bold mb-4 text-primary">
                  Votre équipe technique complète
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>1 à plusieurs développeurs</strong> temps plein selon la mission</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>1 chef de projet technique</strong> dédié pour coordonner</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>1 expert IA</strong> pour optimiser, innover et répondre à vos questions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Flexibilité totale :</strong> Mise de projet de côté en cas de besoins prioritaires</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-primary/10">
                <h3 className="text-xl font-bold mb-4 text-primary">
                  Les avantages d'un forfait mensuel
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Trésorerie préservée :</strong> pas de gros décaissement initial</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Budget clair :</strong> mensualités fixes, prévisibles et étalées</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Souplesse financière :</strong> capacité d'endettement intacte</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Fiscalité optimisée :</strong> OPEX étalées, TVA déductible</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Notre différence - Comparatif vibecoding */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 rounded-full mb-8">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-primary font-medium">Notre différence</span>
            </div>
            
            <h2 className="text-4xl font-bold mb-6">
              Libérez le potentiel de vos équipes
            </h2>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed max-w-4xl mx-auto">
              Cabinet de développement en IA spécialisé dans les <strong>automatisations</strong> et les <strong>logiciels boostés à l'IA</strong>. 
              Notre approche <strong>vibecoding</strong> combine rapidité, fiabilité et sur-mesure.
            </p>
          </div>

          {/* Tableau comparatif approches */}
          <div className="max-w-6xl mx-auto mb-16">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-primary/10 overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-3">
                {/* Colonne No-code/Low-code */}
                <div className="p-8 border-r border-gray-200">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-red-600 mb-2">No-code / Low-code</h3>
                    <p className="text-sm text-muted-foreground">Solutions clés en main</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Fiabilité</span>
                      <span className="text-red-600 font-semibold">Limitée</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Rapidité de mise en place</span>
                      <span className="text-yellow-600 font-semibold">Rapide</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de développement</span>
                      <span className="text-yellow-600 font-semibold">Moyens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Vendor lock-in</span>
                      <span className="text-red-600 font-semibold">Élevé</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Sur-mesure</span>
                      <span className="text-red-600 font-semibold">Très limité</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Évolutivité</span>
                      <span className="text-red-600 font-semibold">Contrainte</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Propriété du code</span>
                      <span className="text-red-600 font-semibold">Non</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de maintenance</span>
                      <span className="text-yellow-600 font-semibold">Moyens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts d'utilisation</span>
                      <span className="text-red-600 font-semibold">Très élevés</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Conformité réglementaire</span>
                      <span className="text-red-600 font-semibold">Limitée</span>
                    </div>
                  </div>
                </div>

                {/* Colonne Code traditionnel */}
                <div className="p-8 border-r border-gray-200">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-600 mb-2">Code traditionnel</h3>
                    <p className="text-sm text-muted-foreground">Développement classique</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Fiabilité</span>
                      <span className="text-green-600 font-semibold">Très bonne</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Rapidité de mise en place</span>
                      <span className="text-red-600 font-semibold">Lente</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de développement</span>
                      <span className="text-red-600 font-semibold">Élevés</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Vendor lock-in</span>
                      <span className="text-green-600 font-semibold">Aucun</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Sur-mesure</span>
                      <span className="text-green-600 font-semibold">Total</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Évolutivité</span>
                      <span className="text-green-600 font-semibold">Maximale</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Propriété du code</span>
                      <span className="text-green-600 font-semibold">Oui</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de maintenance</span>
                      <span className="text-red-600 font-semibold">Élevés</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts d'utilisation</span>
                      <span className="text-green-600 font-semibold">Faibles</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Conformité réglementaire</span>
                      <span className="text-green-600 font-semibold">Totale</span>
                    </div>
                  </div>
                </div>

                {/* Colonne Vibecoding */}
                <div className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-primary mb-2">Notre approche Vibecoding</h3>
                    <p className="text-sm text-primary/80">Le meilleur des deux mondes</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Fiabilité</span>
                      <span className="text-primary font-bold">Excellente</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Rapidité de mise en place</span>
                      <span className="text-primary font-bold">+40% plus rapide</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de développement</span>
                      <span className="text-primary font-bold">-40% moins chers</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Vendor lock-in</span>
                      <span className="text-primary font-bold">Aucun</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Sur-mesure</span>
                      <span className="text-primary font-bold">Sans limites</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Évolutivité</span>
                      <span className="text-primary font-bold">Maximale</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Propriété du code</span>
                      <span className="text-primary font-bold">100% à vous</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts de maintenance</span>
                      <span className="text-primary font-bold">Moyens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Coûts d'utilisation</span>
                      <span className="text-primary font-bold">Faibles</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Conformité réglementaire</span>
                      <span className="text-primary font-bold">Totale</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation vers les pages principales - Conditionnelle */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container mx-auto">
          {showClientContent ? (
            <>
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">
                    De la vision stratégique à l'action.
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Accédez à une vision complète de votre projet de transformation et configurez vos solutions sur-mesure.
                </p>
              </div>
              
              <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Card Synthèse */}
            <Card className="bg-gradient-card shadow-elegant hover:shadow-glow transition-smooth group cursor-pointer border-2 border-transparent hover:border-primary/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-smooth"></div>
              <CardHeader className="pb-6 relative z-10">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-smooth">
                  <BarChart3 className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-2xl text-center mb-4">Votre Hub de pilotage stratégique</CardTitle>
                <CardDescription className="text-center text-base leading-relaxed">
                Prenez des décisions éclairées. Visualisez l'impact complet de votre projet grâce à une analyse consolidée du ROI, des coûts et du planning.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10">
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Analyse financière complète avec ROI détaillé</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Répartition des missions par catégorie</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Projections d'économies sur 3 ans</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>Missions prioritaires par impact</span>
                  </div>
                </div>
                <Button variant="hero" size="lg" className="w-full" asChild>
                  <Link to={`${base}/synthese`}>
                    Voir la synthèse <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Card Missions & Configurateur */}
            <Card className="bg-gradient-card shadow-elegant hover:shadow-glow transition-smooth group cursor-pointer border-2 border-transparent hover:border-accent/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-smooth"></div>
              <CardHeader className="pb-6 relative z-10">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-smooth">
                  <Settings className="w-8 h-8 text-accent" />
                </div>
                <CardTitle className="text-2xl text-center mb-4">Missions & Configurateur</CardTitle>
                <CardDescription className="text-center text-base leading-relaxed">
                  Explorez en détail chaque mission, configurez votre projet sur-mesure et obtenez un devis personnalisé adapté à vos besoins.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10">
                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>Catalogue détaillé de toutes les missions</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>Technologies et méthodes pour chaque projet</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>Configurateur interactif de votre sélection</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>Devis personnalisé et planning optimisé</span>
                  </div>
                </div>
                <Button variant="outline" size="lg" className="w-full border-accent/30 hover:bg-accent/10 hover:text-accent hover:border-accent" asChild>
                  <Link to={`${base}/missions`}>
                    Explorer les missions <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
            </>
          ) : (
            /* Contenu alternatif pour les autres clients */
            <div className="text-center">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-4xl font-bold mb-6">
                  Accédez à votre espace de transformation digitale
                </h2>
                <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                  Votre consultant Ailix a préparé une analyse personnalisée de vos processus et opportunités d'automatisation. 
                  Connectez-vous à votre espace client pour découvrir votre feuille de route sur-mesure.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Button variant="hero" size="lg" asChild>
                    <Link to="/demo-axylis">
                      <ArrowRight className="mr-2 w-5 h-5" />
                      Se connecter à mon espace
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to={clientId ? `${base}/cabinet` : "/cabinet"}>En savoir plus sur Ailix</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bouton flottant de gestion de compte */}
      <AccountFloatingButton />
    </div>
  );
};

export default Home;